import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Dimension } from '@/app/types/cad';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface DimensionEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dimension: Dimension | null;
  blockId: string | null;
  onDimensionUpdate: (blockId: string, dimensionId: string, actualValue: string) => void;
}

export function DimensionEditDialog({
  open,
  onOpenChange,
  dimension,
  blockId,
  onDimensionUpdate,
}: DimensionEditDialogProps) {
  const { t } = useLanguage();
  const [actualValue, setActualValue] = useState('');

  useEffect(() => {
    if (dimension) {
      // If there's already an actualValue, use it; otherwise use the calculated value
      setActualValue(dimension.actualValue || dimension.value);
    }
  }, [dimension]);

  const handleSave = () => {
    if (!dimension || !blockId) return;

    // Only save if different from calculated value
    if (actualValue !== dimension.value) {
      onDimensionUpdate(blockId, dimension.id, actualValue);
    } else {
      // Clear override if it matches calculated value
      onDimensionUpdate(blockId, dimension.id, '');
    }

    onOpenChange(false);
  };

  const handleReset = () => {
    if (!dimension) return;
    setActualValue(dimension.value);
  };

  if (!dimension) return null;

  const calculatedValue = dimension.value;
  const isOverridden = dimension.actualValue && dimension.actualValue !== dimension.value;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('dialogs.dimensionEdit.title')}</DialogTitle>
          <DialogDescription>
            {t('dialogs.dimensionEdit.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Calculated value (read-only) */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">{t('dialogs.dimensionEdit.calculatedLength')}</Label>
            <div className="flex items-center gap-2">
              <Input
                value={calculatedValue}
                disabled
                className="font-mono"
              />
              <span className="text-sm text-gray-500 min-w-[40px]">{dimension.unit}</span>
            </div>
            <p className="text-xs text-gray-500">
              {t('dialogs.dimensionEdit.calculatedDesc')}
            </p>
          </div>

          {/* Actual value (editable) */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">
              {t('dialogs.dimensionEdit.actualLength')} {isOverridden && <span className="text-orange-600">({t('dialogs.dimensionEdit.overridden')})</span>}
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="text"
                value={actualValue}
                onChange={(e) => setActualValue(e.target.value)}
                placeholder={calculatedValue}
                className="font-mono"
              />
              <span className="text-sm text-gray-500 min-w-[40px]">{dimension.unit}</span>
            </div>
            <p className="text-xs text-gray-500">
              {t('dialogs.dimensionEdit.actualDesc')}
            </p>
          </div>

          {/* Visual indicator when overridden */}
          {isOverridden && (
            <div 
              className="p-3 rounded border-l-4" 
              style={{ 
                backgroundColor: 'var(--md-warning-container)',
                borderColor: 'var(--md-warning)',
                color: 'var(--md-on-warning-container)'
              }}
            >
              <div className="text-sm font-medium">⚠️ {t('dialogs.dimensionEdit.overridden')}</div>
              <div className="text-xs mt-1">
                {t('dialogs.dimensionEdit.displayed')}: {dimension.actualValue} {dimension.unit} (statt {calculatedValue} {dimension.unit})
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
          >
            {t('common.reset')}
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
